import { useCallback } from "react";
import { useApolloClient } from "@apollo/client/react";
import { toastManager, type Omittable } from "@salestrak/ui";
import {
  AddressAutocompleteDocument,
  ResolveAddressSuggestionDocument,
} from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

type OpenAddressOnMapTarget = Omittable<{
  wholeAddress: string;
  /** Coordinates already known to the caller (an issue's address entity).
   *  Without them the text is resolved through Places first. */
  latLng?: { lat: number; lng: number } | null;
  /** Lets the map match the exact pin rather than by coordinate equality. */
  addressId?: string | null;
}>;

/**
 * "Open in Map" for any address the app shows: jumps to the workspace map
 * focused on the address (the map's `focusLat`/`focusLng` one-shot finds or
 * creates the pin there and opens its infobox) — never an external maps
 * provider.
 *
 * A custom-field address stores only its text, so with no coordinates the
 * text is geocoded on demand: the first Places suggestion for the whole
 * address, resolved to a canonical address entity. Both calls are billable,
 * which is why a caller that already holds coordinates passes them.
 *
 * `via` names the host control for the `issue.open_address_on_map` event.
 */
export function useOpenAddressOnMap(via: string): (
  target: OpenAddressOnMapTarget,
) => Promise<void> {
  const client = useApolloClient();
  const appNavigate = useAppNavigate();

  return useCallback(
    async (target: OpenAddressOnMapTarget) => {
      let latLng = target.latLng ?? null;
      let addressId = target.addressId ?? null;
      if (!latLng) {
        try {
          const { data } = await client.query({
            query: AddressAutocompleteDocument,
            variables: { query: target.wholeAddress },
          });
          const first = data?.addressAutocomplete[0];
          if (first) {
            const resolved = await client.mutate({
              mutation: ResolveAddressSuggestionDocument,
              variables: {
                placeId: first.placeId,
                description: first.description,
              },
            });
            const address = resolved.data?.resolveAddressSuggestion;
            latLng = address?.latLng ?? null;
            addressId = address?.addressId ?? null;
          }
        } catch {
          latLng = null;
        }
      }
      if (!latLng) {
        toastManager.add({ title: "Address not found", type: "error" });
        return;
      }
      const params = new URLSearchParams({
        focusLat: String(latLng.lat),
        focusLng: String(latLng.lng),
      });
      if (addressId) params.set("addressId", addressId);
      trackEvent({
        context: AnalyticsContext.Issue,
        event: IssueEvent.OpenAddressOnMap,
        data: { via, geocoded: !target.latLng },
      });
      appNavigate.push(`/map?${params.toString()}`);
    },
    [appNavigate, client, via],
  );
}
